import { SearchInventoryItems } from 'admin/InventoryStore'
import { AlertFactory } from 'alerts/Alert'
import { FetchUserTeamMembers, FilterTeamMembers } from 'iam/TeamMemberStore'
import WorkspaceSelectorState from 'iam/views/AuthenticationContainer/WorkspaceSelector/WorkspaceSelectorState'
import { GetCurrentUserWorkspaceMemberships } from 'iam/WorkspaceMembershipStore'
import { FetchDocumentTypes } from 'documents/DocumentTypeStore'
import { JobsLoadFilterItemsAdapter } from 'jobs/infra/JobsLoadFilterItemsAdapter'
import JobSearchStore from 'jobs/views/JobManagement/JobSearchStore'
import { JobStatusFilterOption } from 'jobs/views/JobManagement/JobStatusFilterOption'
import { FindLegends } from 'legends/PublishedLegendStore'
import identity from 'lodash/identity'
import { action, computed, makeObservable, observable } from 'mobx'
import { task } from 'mobx-task'
import { createTransformer } from 'mobx-utils'
import { Subscription, distinctUntilChanged, filter, first } from 'rxjs'
import { Id } from 'types/definitions'
import { toStream } from 'utils/mobx-util'
import { GetCurrentMember } from '../../../../iam/SessionStore'
import { FindUserTeams } from '../../../../iam/TeamStore'
import UserDataStore from '../../../../iam/UserDataStore'
import { SelectFilter } from '../../../../infra/FilterControls/Select/SelectFilter'
import { FilterStorageFactory } from '../../../../infra/FiltersViewModel'
import JobSearchAdapter from '../../../infra/JobSearchAdapter'
import JobSearchViewModel, {
  TicketsAdapter,
} from '../../../infra/JobSearchViewModel'
import SearchableJob from '../../../SearchableJob'
import { JobTicketStore } from '../../../tickets/JobTicketStore'
import { GetActiveJob, JobItemViewModel } from './JobItem/JobItemViewModel'

/**
 * Job list view model.
 */
export class JobListViewModel {
  /**
   * Observable subscription used for disposing on deactivate.
   */
  private subscription = new Subscription()

  /**
   * Whether the control is visible.
   */
  @observable visible = true

  /**
   * Whether we can view opportunities.
   * Used to render the appropriate empty state.
   */
  @observable canViewOpportunities = false

  /**
   * The current loaded active job's workspace.
   */
  private readonly activeJobWorkspace$ = toStream(
    () => this.getActiveJob()?.workspace,
    true,
  ).pipe(filter(Boolean), distinctUntilChanged())

  /**
   * Used for creating a Job Item View Model for each Job instance.
   */
  createJobItemViewModel = createTransformer<SearchableJob, JobItemViewModel>(
    (job) => {
      const vm = new JobItemViewModel(
        job,
        this.getActiveJob,
        this.getCurrentUserWorkspaceMemberships,
        () => this.jobTicketStore.getLatestTicketForJob(job.id),
      )
      vm.activate()
      return vm
    },
    (vm) => vm && vm.deactivate(),
  )

  /**
   * Adapter to load filter items.
   */
  loadFilterItemsAdapter = new JobsLoadFilterItemsAdapter(
    this.getCurrentUserWorkspaceMemberships,
    this.findLegends,
    this.findUserTeams,
    this.searchServiceItems,
    this.fetchDocumentTypes,
  )

  /**
   * Adapter for retrieving tickets.
   */
  ticketsAdapter: TicketsAdapter = {
    fetchLatestOpenTicketsForJobs:
      this.jobTicketStore.fetchLatestOpenTicketsForJobs,
  }

  /**
   * Job searcher.
   */
  searcher = new JobSearchViewModel(
    this.jobFetcher,
    this.loadFilterItemsAdapter,
    this.ticketsAdapter,
    this.newAlert,
    // Don't apply client-side filter inside the searcher.
    // Otherwise, we would end up showing a Completely Empty State
    // when there may very well be jobs in the search result.
    identity,
    this.getCurrentMember,
    this.userDataStore,
    this.getCurrentUserWorkspaceMemberships,
    this.filterTeamMembers,
    this.createFilterStorage('JobList'),
  )

  /**
   * Toggles the visibility of the list.
   */
  @action.bound
  toggleVisibility() {
    this.visible = !this.visible
  }

  /**
   * Constructor.
   */
  constructor(
    private jobFetcher: JobSearchAdapter,
    private jobTicketStore: JobTicketStore,
    private newAlert: AlertFactory,
    private fetchUserTeamMembers: FetchUserTeamMembers,
    private findLegends: FindLegends,
    private getActiveJob: GetActiveJob,
    private getCurrentUserWorkspaceMemberships: GetCurrentUserWorkspaceMemberships,
    private probeCanViewOpportunities: () => Promise<boolean>,
    private getCurrentMember: GetCurrentMember,
    private userDataStore: UserDataStore,
    private createFilterStorage: FilterStorageFactory,
    private filterTeamMembers: FilterTeamMembers,
    private canEditDraftJobs: () => boolean,
    private jobSearchStore: JobSearchStore,
    private findUserTeams: FindUserTeams,
    private searchServiceItems: SearchInventoryItems,
    private workspaceSelectorState: WorkspaceSelectorState,
    private fetchDocumentTypes: FetchDocumentTypes,
  ) {
    makeObservable(this)
  }

  /**
   * Job item view models.
   */
  @computed
  get jobs() {
    return this.searcher.jobs.map(this.createJobItemViewModel)
  }

  /**
   * Whether we can edit draft jobs as an admin.
   */
  get canEditDraftJobsAsAdmin() {
    return this.canEditDraftJobs()
  }

  /**
   * Activates the view model.
   */
  activate = task(async () => {
    await this.fetchUserTeamMembers()
    const canViewOpportunities = await this.probeCanViewOpportunities()
    this.setCanViewOpportunities(canViewOpportunities)

    this.prepareSubscriptions()
  })

  /*
   * Searches jobs.
   */
  search = (searchTerm: string) => {
    const activeJob = this.getActiveJob()

    if (activeJob) {
      this.setDefaultWorkspaceFilter(activeJob.workspace.id, searchTerm)
    }

    this.searcher.searchText.change(searchTerm)
  }

  /**
   * Prepares the subscriptions.
   */
  private prepareSubscriptions() {
    if (this.canEditDraftJobsAsAdmin) {
      this.subscription.add(
        // When a job is created, add it to the top of the list.
        // This is a workaround for the fact that the job searcher is not
        // processing the JobCreate event when filters are applied.
        this.jobSearchStore.jobEvents$
          .pipe(filter((e) => e.type === 'JobCreated'))
          .subscribe((e) => {
            // We don't want to add the job to the list if it's already there or
            // if the user is searching for something.
            if (
              this.searcher.scroller.items.includes(e.job) ||
              this.searcher.searchText.value !== ''
            ) {
              return
            }

            this.searcher.scroller.items.unshift(e.job)
          }),
      )
    }

    // Activate searcher until we have a job loaded to determine which workspace it
    // belongs to.
    this.activeJobWorkspace$.pipe(first()).subscribe((workspace) => {
      this.searcher.activate()
      this.setDefaultJobStatusFilter()
      this.setDefaultWorkspaceFilter(
        workspace.id,
        this.searcher.searchText.value,
      )
    })
  }

  /**
   * Called when the view model is not being actively used.
   */
  deactivate() {
    this.searcher.deactivate()
    this.subscription.unsubscribe()
  }

  /**
   * Sets whether we can view opportunities.
   *
   * @param canView
   */
  @action
  private setCanViewOpportunities(canView: boolean) {
    this.canViewOpportunities = canView
  }

  /**
   * Defaults search filter by job status.
   * @private
   */
  @action
  private setDefaultJobStatusFilter() {
    this.searcher.filters.addFilter('jobStatus')
    const filter = this.searcher.filters.getFilter('jobStatus') as SelectFilter<
      string,
      JobStatusFilterOption
    >
    const filterBy = [
      JobStatusFilterOption.InProgress,
      JobStatusFilterOption.Unclaimed,
      JobStatusFilterOption.InfoGathering,
      JobStatusFilterOption.Pending,
      JobStatusFilterOption.OnHold,
      JobStatusFilterOption.Idle,
    ]

    filter.selected = this.canEditDraftJobsAsAdmin
      ? [JobStatusFilterOption.Onboarding, ...filterBy]
      : filterBy
  }

  /**
   * Defaults search filter by the active job's workspace if possible.
   */
  @action
  private setDefaultWorkspaceFilter(workspaceId: Id, searchTerm?: string) {
    if (this.searcher.isUserTypePro) {
      return
    }

    const workspaceFilter = this.searcher.filters.getFilter(
      'workspaces',
    ) as SelectFilter<string, Id>

    // Always default to the current workspace if the user is not a pro user.
    // Otherwise, show all jobs from all workspaces.
    workspaceFilter.selected =
      !searchTerm || !this.workspaceSelectorState.isDefaultToAllWorkspaces
        ? [workspaceId]
        : []
  }
}
